import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import lazyRetry, { isChunkLoadError, attemptChunkReload } from './utils/lazyRetry';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './utils/queryClient';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { CheckCircle2, XCircle, Info } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
// Side-effect import: registers the api-error / js-error listeners in
// utils/trackAction.js. Without it the friction tracking would only be live on
// the handful of pages that happen to import track() for their own buttons —
// i.e. exactly the pages that already work well enough to be instrumented.
import './utils/trackAction';
import LiveActivityPinger from './components/LiveActivityPinger';
import LiveCaptureAgent from './components/LiveCaptureAgent';
import i18n from './i18n';
import reportClientError, { installGlobalErrorReporting } from './utils/reportClientError';
import { installDatePickerOpener } from './utils/dateInput';
import { installFormValidityI18n } from './utils/formValidityI18n';

// Eager — needed for first paint
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import Layout from './components/layout/Layout';
import LandingRedirect from './components/LandingRedirect';
import { ChatProvider } from './contexts/ChatContext';
import { CallProvider } from './contexts/CallContext';
import { RemoteAssistProvider } from './contexts/RemoteAssistContext';
import { LiveUpdatesProvider } from './contexts/LiveUpdatesContext';

// Lazy — loaded on demand
const TicketsPage = lazyRetry(() => import('./pages/TicketsPage'));
const TicketDetailPage = lazyRetry(() => import('./pages/TicketDetailPage'));
const AgentsPage = lazyRetry(() => import('./pages/AgentsPage'));
const PortalPermissionsPage = lazyRetry(() => import('./pages/PortalPermissionsPage'));
const GameAdminPage = lazyRetry(() => import('./pages/GameAdminPage'));
const ProdePage = lazyRetry(() => import('./pages/ProdePage'));
const GamePage = lazyRetry(() => import('./pages/GamePage'));
const AgentDashboardsPage = lazyRetry(() => import('./pages/AgentDashboardsPage'));
const TeamPanelPage = lazyRetry(() => import('./pages/TeamPanelPage'));
const HRPage = lazyRetry(() => import('./pages/HRPage'));
const MyPayrollPage = lazyRetry(() => import('./pages/MyPayrollPage'));
const SettingsPage = lazyRetry(() => import('./pages/SettingsPage'));
const ProfilePage = lazyRetry(() => import('./pages/ProfilePage'));
const CompaniesPage = lazyRetry(() => import('./pages/CompaniesPage'));
const MyTicketsPage = lazyRetry(() => import('./pages/MyTicketsPage'));
const PrivateInboxPage = lazyRetry(() => import('./pages/PrivateInboxPage'));
const BulkReviewQueuePage = lazyRetry(() => import('./pages/BulkReviewQueuePage'));
const AgentPresencePage = lazyRetry(() => import('./pages/AgentPresencePage'));
const TrashPage = lazyRetry(() => import('./pages/TrashPage'));
const SpamPage = lazyRetry(() => import('./pages/SpamPage'));
const NotificationsPage = lazyRetry(() => import('./pages/NotificationsPage'));
const InboundRulesPage = lazyRetry(() => import('./pages/InboundRulesPage'));
const PapeleraPage = lazyRetry(() => import('./pages/PapeleraPage'));
const CompanyTicketsPage = lazyRetry(() => import('./pages/CompanyTicketsPage'));
const CompanyKnowledgePage = lazyRetry(() => import('./pages/CompanyKnowledgePage'));
const MonitorAssetsPage = lazyRetry(() => import('./pages/MonitorAssetsPage'));
const MonitorAssetDetailPage = lazyRetry(() => import('./pages/MonitorAssetDetailPage'));
const SonosPage = lazyRetry(() => import('./pages/SonosPage'));
const RemoteAccessPage = lazyRetry(() => import('./pages/RemoteAccessPage'));
const AgentActivityPage = lazyRetry(() => import('./pages/AgentActivityPage'));
const UsageHotspotsPage = lazyRetry(() => import('./pages/UsageHotspotsPage'));
const SessionReplayPage = lazyRetry(() => import('./pages/SessionReplayPage'));
const AnnouncementsAdminPage = lazyRetry(() => import('./pages/AnnouncementsAdminPage'));
const WorkerHealthPage = lazyRetry(() => import('./pages/WorkerHealthPage'));
const SystemActivityPage = lazyRetry(() => import('./pages/SystemActivityPage'));
const RemoteAssistPage = lazyRetry(() => import('./pages/RemoteAssistPage'));
const RemoteAssistViewer = lazyRetry(() => import('./pages/RemoteAssistViewer'));
const RemoteAssistCustomer = lazyRetry(() => import('./pages/RemoteAssistCustomer'));
const VoiceCallCustomer = lazyRetry(() => import('./pages/VoiceCallCustomer'));
const IntegrationChat = lazyRetry(() => import('./pages/IntegrationChat'));
const VoiceCallsPage = lazyRetry(() => import('./pages/VoiceCallsPage'));
const LiveAssistProPage = lazyRetry(() => import('./pages/LiveAssistProPage'));
const LiveAssistProPresenter = lazyRetry(() => import('./pages/LiveAssistProPresenter'));
const LiveAssistProViewer = lazyRetry(() => import('./pages/LiveAssistProViewer'));
const CCTVPage = lazyRetry(() => import('./pages/CCTVPage'));
const VideoWallPage = lazyRetry(() => import('./pages/VideoWallPage'));
const SupplierAccountsPage = lazyRetry(() => import('./pages/SupplierAccounts/SupplierAccountsPage'));
const MyPlatformsPage = lazyRetry(() => import('./pages/MyPlatforms/MyPlatformsPage'));
const OffboardingPage = lazyRetry(() => import('./pages/OffboardingPage'));
const WebsiteReportsPage = lazyRetry(() => import('./pages/WebsiteReportsPage'));
const ItCostsPage = lazyRetry(() => import('./pages/ItCostsPage'));
const CockpitPage = lazyRetry(() => import('./pages/CockpitPage'));
const CampaignsPage = lazyRetry(() => import('./pages/CampaignsPage'));
const DataIntegrityPage = lazyRetry(() => import('./pages/DataIntegrityPage'));
const ApiTokensPage = lazyRetry(() => import('./pages/ApiTokensPage'));
const SmartHomesPage = lazyRetry(() => import('./pages/SmartHomesPage'));
const RecruitingPage = lazyRetry(() => import('./pages/RecruitingPage'));
const PortalStudioPage = lazyRetry(() => import('./pages/PortalStudioPage'));
const WhatsAppAvatarsPage = lazyRetry(() => import('./pages/WhatsAppAvatarsPage'));
const SiteContentPage = lazyRetry(() => import('./pages/SiteContentPage'));
const TwoFaCodesPage = lazyRetry(() => import('./pages/TwoFaCodesPage'));
const ClientAccessPage = lazyRetry(() => import('./pages/ClientAccessPage'));
const TicketImportPage = lazyRetry(() => import('./pages/TicketImportPage'));
const StoragePage = lazyRetry(() => import('./pages/StoragePage'));
const EmailMonitorPage = lazyRetry(() => import('./pages/EmailMonitorPage'));
const ValidationPage = lazyRetry(() => import('./pages/ValidationPage'));
const SuppliesPage = lazyRetry(() => import('./pages/SuppliesPage'));
const SuppliesAnalyticsPage = lazyRetry(() => import('./pages/SuppliesAnalyticsPage'));
const ClientSuppliesView = lazyRetry(() => import('./pages/ClientSuppliesView'));
const TonerInventoryPage = lazyRetry(() => import('./pages/TonerInventoryPage'));
const ChatPage = lazyRetry(() => import('./pages/ChatPage'));
const KnowledgeBasePage = lazyRetry(() => import('./pages/KnowledgeBasePage'));
const RagSearchPage = lazyRetry(() => import('./pages/RagSearchPage'));
const KBArticlePage = lazyRetry(() => import('./pages/KBArticlePage'));
const KBArticleEditorPage = lazyRetry(() => import('./pages/KBArticleEditorPage'));
const InvoicingPage = lazyRetry(() => import('./pages/InvoicingPage'));
const BillingEntitiesPage = lazyRetry(() => import('./pages/BillingEntitiesPage'));
const AccountsPage = lazyRetry(() => import('./pages/AccountsPage'));
const AccountDetailPage = lazyRetry(() => import('./pages/AccountDetailPage'));
const BudgetsPage = lazyRetry(() => import('./pages/BudgetsPage'));
const BudgetEditorPage = lazyRetry(() => import('./pages/BudgetEditorPage'));
const VaultPage = lazyRetry(() => import('./pages/VaultPage'));
const CalendarPage = lazyRetry(() => import('./pages/CalendarPage'));
const WhatsAppSettingsPage = lazyRetry(() => import('./pages/WhatsAppSettingsPage'));
const AgentRatingsPage = lazyRetry(() => import('./pages/AgentRatingsPage'));
const TicketAuditLogPage = lazyRetry(() => import('./pages/TicketAuditLogPage'));
const DeploymentsPage = lazyRetry(() => import('./pages/DeploymentsPage'));
const TopologyPage = lazyRetry(() => import('./pages/TopologyPage'));
const ResourceMonitorPage = lazyRetry(() => import('./pages/ResourceMonitorPage'));
const ProjectsPage = lazyRetry(() => import('./pages/ProjectsPage'));
const PersonnelPage = lazyRetry(() => import('./pages/PersonnelPage'));
const PublicPersonnelForm = lazyRetry(() => import('./pages/PublicPersonnelForm'));
const SumatePage = lazyRetry(() => import('./pages/SumatePage'));
const BackupMonitorPage = lazyRetry(() => import('./pages/BackupMonitorPage'));
const OpsMonitorPage = lazyRetry(() => import('./pages/OpsMonitorPage'));
const CloudCostsPage = lazyRetry(() => import('./pages/CloudCostsPage'));
const GoogleBusinessPage = lazyRetry(() => import('./pages/GoogleBusinessPage'));
const ToolCostsPage = lazyRetry(() => import('./pages/ToolCostsPage'));
const CostsHubPage = lazyRetry(() => import('./pages/CostsHub/CostsHubPage'));
const DmarcPage = lazyRetry(() => import('./pages/DmarcPage'));
const VpsHealthPage = lazyRetry(() => import('./pages/VpsHealthPage'));
const IpIntelligencePage = lazyRetry(() => import('./pages/IpIntelligencePage'));
const VpsHostDetailPage = lazyRetry(() => import('./pages/VpsHostDetailPage'));
const BackupJobDetailPage = lazyRetry(() => import('./pages/BackupJobDetailPage'));
const ErrandsPage = lazyRetry(() => import('./pages/ErrandsPage'));
const RequestsPage = lazyRetry(() => import('./pages/RequestsPage'));
const ReportsPage = lazyRetry(() => import('./pages/ReportsPage'));
const ReportReviewPage = lazyRetry(() => import('./pages/ReportReviewPage'));
const AgentActionsPage = lazyRetry(() => import('./pages/AgentActions/AgentActionsPage'));
const DiagnosticPage = lazyRetry(() => import('./pages/DiagnosticPage'));
const DiagnosticsAdminPage = lazyRetry(() => import('./pages/DiagnosticsAdminPage'));
const AssetsPage = lazyRetry(() => import('./pages/AssetsPage'));

// /infrastructure no es una página propia: Infraestructura vive como pestaña de Inventario.
// Pero varios links la usan (compartir la ficha, "ir al switch" desde la ubicación de un
// equipo, Empresas → infraestructura) y caían en el comodín, que manda al inicio. Se
// redirige a la pestaña conservando los parámetros (id abre la ficha; company_id filtra).
function InfrastructureRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'infrastructure');
  return <Navigate to={`/assets?${params.toString()}`} replace />;
}
const WorkshopPage = lazyRetry(() => import('./pages/WorkshopPage'));
const PrepaidLinesPage = lazyRetry(() => import('./pages/PrepaidLinesPage'));
const RacksPage = lazyRetry(() => import('./pages/RacksPage'));
const LegacySheetsPage = lazyRetry(() => import('./pages/LegacySheetsPage'));
const MercadoLibrePage = lazyRetry(() => import('./pages/MercadoLibrePage'));
const QuickLaunchPage = lazyRetry(() => import('./pages/QuickLaunchPage'));
const AccessControlPage = lazyRetry(() => import('./pages/AccessControlPage'));
const AttendancePage = lazyRetry(() => import('./pages/AttendancePage'));
const FilePermissionsPage = lazyRetry(() => import('./pages/FilePermissionsPage'));
const PortalFilePermissionsPage = lazyRetry(() => import('./pages/portal/PortalFilePermissionsPage'));
const PublicFilePermissionReviewPage = lazyRetry(() => import('./pages/PublicFilePermissionReviewPage'));
const ConfigurationsPage = lazyRetry(() => import('./pages/ConfigurationsPage'));
const EmailAccountsPage = lazyRetry(() => import('./pages/EmailAccountsPage'));
const MonitoringPage = lazyRetry(() => import('./pages/MonitoringPage'));
const MonitoringEndpointPage = lazyRetry(() => import('./pages/MonitoringEndpointPage'));
const MonitoringSettingsPage = lazyRetry(() => import('./pages/MonitoringSettingsPage'));
const AiDashboardPage = lazyRetry(() => import('./pages/AiDashboardPage'));
const GrowthPage = lazyRetry(() => import('./pages/GrowthPage'));
const MonitoringTeamPage = lazyRetry(() => import('./pages/MonitoringTeamPage'));
const MonitoringInstallPage = lazyRetry(() => import('./pages/MonitoringInstallPage'));
const ForgotPasswordPage = lazyRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('./pages/ResetPasswordPage'));

// Portal — lazy
const PortalLayout = lazyRetry(() => import('./components/layout/PortalLayout'));
const PortalLoginPage = lazyRetry(() => import('./pages/portal/PortalLoginPage'));
const PortalDashboardPage = lazyRetry(() => import('./pages/portal/PortalDashboardPage'));
const PortalNotificationsPage = lazyRetry(() => import('./pages/portal/PortalNotificationsPage'));
const PortalTicketsPage = lazyRetry(() => import('./pages/portal/PortalTicketsPage'));
const PortalTicketDetailPage = lazyRetry(() => import('./pages/portal/PortalTicketDetailPage'));
const PortalMonitorsPage = lazyRetry(() => import('./pages/portal/PortalMonitorsPage'));
const PortalKBPage = lazyRetry(() => import('./pages/portal/PortalKBPage'));
const PortalSonosPage = lazyRetry(() => import('./pages/portal/PortalSonosPage'));
const PortalUpsPage = lazyRetry(() => import('./pages/portal/PortalUpsPage'));
const PortalResourceMonitorPage = lazyRetry(() => import('./pages/portal/PortalResourceMonitorPage'));
const PortalMonitorDetailPage = lazyRetry(() => import('./pages/portal/PortalMonitorDetailPage'));
const PortalVaultPage = lazyRetry(() => import('./pages/portal/PortalVaultPage'));
const PortalDeploymentsPage = lazyRetry(() => import('./pages/portal/PortalDeploymentsPage'));
const PortalProjectsPage = lazyRetry(() => import('./pages/portal/PortalProjectsPage'));
const PortalBackupsPage = lazyRetry(() => import('./pages/portal/PortalBackupsPage'));
const PortalWebReviewPage = lazyRetry(() => import('./pages/portal/PortalWebReviewPage'));
const PortalBackupDetailPage = lazyRetry(() => import('./pages/portal/PortalBackupDetailPage'));
const PortalReportsPage = lazyRetry(() => import('./pages/portal/PortalReportsPage'));
const PortalConfigurationsPage = lazyRetry(() => import('./pages/portal/PortalConfigurationsPage'));
const PortalConfigurationDetailPage = lazyRetry(() => import('./pages/portal/PortalConfigurationDetailPage'));
const Portal2FAPage = lazyRetry(() => import('./pages/portal/Portal2FAPage'));
const ShowcasePage = lazyRetry(() => import('./pages/ShowcasePage'));
const ShowcasePageEN = lazyRetry(() => import('./pages/ShowcasePageEN'));
const DemoVisitsPage = lazyRetry(() => import('./pages/DemoVisitsPage'));
const PublicStatusPage = lazyRetry(() => import('./pages/PublicStatusPage'));
const PublicKBArticlePage = lazyRetry(() => import('./pages/PublicKBArticlePage'));
const ContactEnrichPage = lazyRetry(() => import('./pages/ContactEnrichPage'));
const AssetPublicPage = lazyRetry(() => import('./pages/AssetPublicPage'));
const PortalAssetsPage = lazyRetry(() => import('./pages/portal/PortalAssetsPage'));
const PortalContactsPage = lazyRetry(() => import('./pages/portal/PortalContactsPage'));
const PortalBudgetsPage = lazyRetry(() => import('./pages/portal/PortalBudgetsPage'));
const PortalCalendarPage = lazyRetry(() => import('./pages/portal/PortalCalendarPage'));
const PortalVisitsPage = lazyRetry(() => import('./pages/portal/PortalVisitsPage'));
const PortalModuleLocked = lazyRetry(() => import('./pages/portal/PortalModuleLocked'));
const PortalEmailAccountsPage = lazyRetry(() => import('./pages/portal/PortalEmailAccountsPage'));
const PortalMonitoringPage = lazyRetry(() => import('./pages/portal/PortalMonitoringPage'));
const PortalAttendancePage = lazyRetry(() => import('./pages/portal/PortalAttendancePage'));
const PortalCashPage = lazyRetry(() => import('./pages/portal/PortalCashPage'));
const PortalCctvPage = lazyRetry(() => import('./pages/portal/PortalCctvPage'));
const PortalDmarcPage = lazyRetry(() => import('./pages/portal/PortalDmarcPage'));
const PortalCloudCostsPage = lazyRetry(() => import('./pages/portal/PortalCloudCostsPage'));
const PortalRacksPage = lazyRetry(() => import('./pages/portal/PortalRacksPage'));
const PortalGoogleBusinessPage = lazyRetry(() => import('./pages/portal/PortalGoogleBusinessPage'));
const PortalItCostsPage = lazyRetry(() => import('./pages/portal/PortalItCostsPage'));
const PortalMonitoringEndpointPage = lazyRetry(() => import('./pages/portal/PortalMonitoringEndpointPage'));
const VPNPage = lazyRetry(() => import('./pages/VPNPage'));
const PortalVPNPage = lazyRetry(() => import('./pages/portal/PortalVPNPage'));
const PortalConnectivitiesPage = lazyRetry(() => import('./pages/portal/PortalConnectivitiesPage'));
const FeedbackPage = lazyRetry(() => import('./pages/FeedbackPage'));
const ChangelogPage = lazyRetry(() => import('./pages/ChangelogPage'));
const FeedbackAdminPage = lazyRetry(() => import('./pages/FeedbackAdminPage'));
const TelegramRoutesPage = lazyRetry(() => import('./pages/TelegramRoutesPage'));
const ProceduresPage = lazyRetry(() => import('./pages/ProceduresPage'));
const ProvisioningPage = lazyRetry(() => import('./pages/ProvisioningPage'));
const WebReviewPage = lazyRetry(() => import('./pages/WebReviewPage'));

// queryClient imported from utils/queryClient.js

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Este es el que se lleva la app entera: el que hay que enterarse SÍ o SÍ.
    reportClientError({ error, componentStack: info.componentStack, boundary: 'App' });
    // A failed lazy import (stale tab after a deploy whose chunk hash changed,
    // or a transient fetch failure lazyRetry couldn't recover) lands here. Don't
    // make the user click "reload" — auto-reload to pull a fresh index.html +
    // matching chunks. Rolling-window guarded (attemptChunkReload) so it heals
    // across every deploy of the day but can't hard-loop on a broken build; when
    // it declines we fall through to the manual button below.
    if (isChunkLoadError(error)) attemptChunkReload();
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', padding: 32, background: 'var(--c-bg)', color: 'var(--c-text)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>:(</div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{i18n.t('errorBoundary.title')}</h1>
          <p style={{ fontSize: 14, color: 'var(--c-text-muted)', marginBottom: 24, textAlign: 'center', maxWidth: 400 }}>
            {i18n.t('errorBoundary.message')}
          </p>
          <pre style={{
            fontSize: 12, color: '#ef4444', background: 'var(--c-surface-2)',
            padding: 16, borderRadius: 8, maxWidth: 500, overflow: 'auto', marginBottom: 24,
            border: '1px solid var(--c-border)',
          }}>
            {this.state.error?.message || 'Unknown error'}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: 'var(--c-brand)', color: '#fff', fontSize: 14, fontWeight: 500,
            }}
          >
            {i18n.t('errorBoundary.reload')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PrivateRoute = ({ children, requireAdmin, requireManager, requireUnscoped, requireModule }) => {
  const { user, loading, isAdmin, isDemo, isManager, isScoped, isClient, hasModule } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Client users should be redirected to portal
  if (isClient) return <Navigate to="/portal" replace />;
  // Demo users get full visibility (like admin) to all pages
  if (!isDemo) {
    if (requireAdmin && !isAdmin) return <Navigate to="/" replace />;
    if (requireManager && !isManager) return <Navigate to="/" replace />;
    if (requireUnscoped && isScoped) return <Navigate to="/" replace />;
  }
  if (requireModule && !hasModule(requireModule)) return <Navigate to="/" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isClient } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user && isClient) return <Navigate to="/portal" replace />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

const PortalPublicRoute = ({ children }) => {
  const { user, loading, isClient } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user && isClient) return <Navigate to="/portal" replace />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

const PortalPrivateRoute = ({ children }) => {
  const { user, loading, isClient } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to={isClientDomain ? '/login' : '/portal/login'} replace />;
  if (!isClient) return <Navigate to="/" replace />;
  return children;
};

// Detect client portal subdomain — serve portal routes at root level
const isClientDomain = window.location.hostname === 'clientes.ayudatecno.com.ar';

const PageSpinner = () => <div className="loading-screen"><div className="spinner" /></div>;

function AppRoutes() {
  return (
    <Suspense fallback={<PageSpinner />}>
    <Routes>
      {/* Public pages (no auth) */}
      <Route path="/demo" element={<ShowcasePage />} />
      <Route path="/demo/en" element={<ShowcasePageEN />} />
      <Route path="/status/:token" element={<PublicStatusPage />} />
      <Route path="/contact-info/:token" element={<ContactEnrichPage />} />
      <Route path="/personnel-form/:token" element={<PublicPersonnelForm />} />
      {/* Postulaciones desde el sitio — destino del botón "Sumate a nuestro soporte" */}
      <Route path="/sumate" element={<SumatePage />} />
      <Route path="/diag/:token" element={<DiagnosticPage />} />
      <Route path="/assets/:tag" element={<AssetPublicPage />} />
      <Route path="/assist/:token" element={<RemoteAssistCustomer />} />
      <Route path="/llamada/:token" element={<VoiceCallCustomer />} />
      {/* Chat embebido en una plataforma integrada: público, todo colgado del token de sesión. */}
      <Route path="/chat/:token" element={<IntegrationChat />} />
      <Route path="/ardata-assist/:token" element={<LiveAssistProViewer />} />
      <Route path="/public/kb/:token" element={<PublicKBArticlePage />} />
      <Route path="/file-permissions/review/:token" element={<PublicFilePermissionReviewPage />} />

      {/* Portal routes — accessible via /portal/* OR at root on clientes.ayudatecno.com.ar */}
      <Route path="/portal/login" element={<PortalPublicRoute><PortalLoginPage /></PortalPublicRoute>} />
      <Route path="/portal/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/portal/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/portal" element={<PortalPrivateRoute><PortalLayout /></PortalPrivateRoute>}>
        <Route index element={<PortalDashboardPage />} />
        <Route path="notifications" element={<PortalNotificationsPage />} />
        <Route path="tickets" element={<PortalTicketsPage />} />
        <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
        <Route path="kb" element={<PortalKBPage />} />
        <Route path="contacts" element={<PortalContactsPage />} />
        <Route path="monitors" element={<PortalMonitorsPage />} />
        <Route path="sonos" element={<PortalSonosPage />} />
        <Route path="monitors/:id" element={<PortalMonitorDetailPage />} />
        <Route path="cctv" element={<PortalCctvPage />} />
        <Route path="ups" element={<PortalUpsPage />} />
        <Route path="resource-monitor" element={<PortalResourceMonitorPage />} />
        <Route path="resource-monitor/:hostId" element={<PortalResourceMonitorPage />} />
        <Route path="connectivities" element={<PortalConnectivitiesPage />} />
        <Route path="vault" element={<PortalVaultPage />} />
        <Route path="backups" element={<PortalBackupsPage />} />
        <Route path="web-review" element={<PortalWebReviewPage />} />
        <Route path="backups/:id" element={<PortalBackupDetailPage />} />
        <Route path="deployments" element={<PortalDeploymentsPage />} />
        <Route path="deployments/:projectId" element={<PortalDeploymentsPage />} />
        <Route path="projects" element={<PortalProjectsPage />} />
        <Route path="projects/:id" element={<PortalProjectsPage />} />
        <Route path="reports" element={<PortalReportsPage />} />
        <Route path="assets" element={<PortalAssetsPage />} />
        <Route path="calendar" element={<PortalCalendarPage />} />
        <Route path="visits" element={<PortalVisitsPage />} />
        {/* Modules listed in the nav but NOT openable — signage only.
            The module stays disabled server-side (requirePortalModule 403s);
            this route only explains why. See resolvePortalLockedModules. */}
        <Route path="locked/:moduleKey" element={<PortalModuleLocked />} />
        <Route path="configurations" element={<PortalConfigurationsPage />} />
        <Route path="configurations/:id" element={<PortalConfigurationDetailPage />} />
        <Route path="2fa" element={<Portal2FAPage />} />
        <Route path="email-accounts" element={<PortalEmailAccountsPage />} />
        <Route path="budgets" element={<PortalBudgetsPage />} />
        <Route path="monitoring" element={<PortalMonitoringPage />} />
        <Route path="monitoring/:id" element={<PortalMonitoringEndpointPage />} />
            <Route path="attendance" element={<PortalAttendancePage />} />
            <Route path="cash" element={<PortalCashPage />} />
        <Route path="dmarc" element={<PortalDmarcPage />} />
        <Route path="cloud-costs" element={<PortalCloudCostsPage />} />
        <Route path="racks" element={<PortalRacksPage />} />
        <Route path="google-business" element={<PortalGoogleBusinessPage />} />
        <Route path="it-costs" element={<PortalItCostsPage />} />
        <Route path="vpn" element={<PortalVPNPage />} />
        <Route path="file-permissions" element={<PortalFilePermissionsPage />} />
        <Route path="file-permissions/:shareId" element={<PortalFilePermissionsPage />} />
      </Route>

      {/* Client subdomain: serve portal at root paths too */}
      {isClientDomain && (
        <>
          <Route path="/login" element={<PortalPublicRoute><PortalLoginPage /></PortalPublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/" element={<PortalPrivateRoute><PortalLayout /></PortalPrivateRoute>}>
            <Route index element={<PortalDashboardPage />} />
            <Route path="notifications" element={<PortalNotificationsPage />} />
            <Route path="tickets" element={<PortalTicketsPage />} />
            <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
            <Route path="monitors" element={<PortalMonitorsPage />} />
            <Route path="sonos" element={<PortalSonosPage />} />
            <Route path="monitors/:id" element={<PortalMonitorDetailPage />} />
            <Route path="cctv" element={<PortalCctvPage />} />
            <Route path="ups" element={<PortalUpsPage />} />
            <Route path="resource-monitor" element={<PortalResourceMonitorPage />} />
            <Route path="resource-monitor/:hostId" element={<PortalResourceMonitorPage />} />
            <Route path="connectivities" element={<PortalConnectivitiesPage />} />
            <Route path="vault" element={<PortalVaultPage />} />
            <Route path="backups" element={<PortalBackupsPage />} />
            <Route path="web-review" element={<PortalWebReviewPage />} />
            <Route path="backups/:id" element={<PortalBackupDetailPage />} />
            <Route path="deployments" element={<PortalDeploymentsPage />} />
            <Route path="deployments/:projectId" element={<PortalDeploymentsPage />} />
            <Route path="reports" element={<PortalReportsPage />} />
            <Route path="assets" element={<PortalAssetsPage />} />
            <Route path="calendar" element={<PortalCalendarPage />} />
            <Route path="visits" element={<PortalVisitsPage />} />
            {/* Modules listed in the nav but NOT openable — signage only.
                The module stays disabled server-side (requirePortalModule 403s);
                this route only explains why. See resolvePortalLockedModules. */}
            <Route path="locked/:moduleKey" element={<PortalModuleLocked />} />
            <Route path="configurations" element={<PortalConfigurationsPage />} />
            <Route path="configurations/:id" element={<PortalConfigurationDetailPage />} />
            <Route path="2fa" element={<Portal2FAPage />} />
            <Route path="email-accounts" element={<PortalEmailAccountsPage />} />
            <Route path="budgets" element={<PortalBudgetsPage />} />
            <Route path="monitoring" element={<PortalMonitoringPage />} />
            <Route path="monitoring/:id" element={<PortalMonitoringEndpointPage />} />
            <Route path="attendance" element={<PortalAttendancePage />} />
            <Route path="cash" element={<PortalCashPage />} />
            <Route path="dmarc" element={<PortalDmarcPage />} />
            <Route path="cloud-costs" element={<PortalCloudCostsPage />} />
        <Route path="racks" element={<PortalRacksPage />} />
        <Route path="google-business" element={<PortalGoogleBusinessPage />} />
            <Route path="it-costs" element={<PortalItCostsPage />} />
            <Route path="vpn" element={<PortalVPNPage />} />
            <Route path="file-permissions" element={<PortalFilePermissionsPage />} />
            <Route path="file-permissions/:shareId" element={<PortalFilePermissionsPage />} />
          </Route>
        </>
      )}

      {/* Internal routes (only on main domain) */}
      {!isClientDomain && (
        <>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/" element={<PrivateRoute><ChatProvider><LiveUpdatesProvider><CallProvider><RemoteAssistProvider><Layout /></RemoteAssistProvider></CallProvider></LiveUpdatesProvider></ChatProvider></PrivateRoute>}>
            <Route index element={<LandingRedirect><DashboardPage /></LandingRedirect>} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="client-access" element={<PrivateRoute requireAdmin><ClientAccessPage /></PrivateRoute>} />
            <Route path="ticket-import" element={<PrivateRoute requireAdmin><TicketImportPage /></PrivateRoute>} />
            <Route path="storage" element={<PrivateRoute requireAdmin><StoragePage /></PrivateRoute>} />
            <Route path="companies/:companyId/tickets" element={<CompanyTicketsPage />} />
            <Route path="companies/:companyId/knowledge" element={<CompanyKnowledgePage />} />
            <Route path="monitor" element={<MonitorAssetsPage />} />
            <Route path="monitor/:id" element={<MonitorAssetDetailPage />} />
            <Route path="sonos" element={<SonosPage />} />
            <Route path="remote-access" element={<PrivateRoute requireModule="remote_access"><RemoteAccessPage /></PrivateRoute>} />
            <Route path="remote-assist" element={<PrivateRoute requireModule="remote_access"><RemoteAssistPage /></PrivateRoute>} />
            <Route path="remote-assist/:id" element={<PrivateRoute requireModule="remote_access"><RemoteAssistViewer /></PrivateRoute>} />
            <Route path="llamadas" element={<PrivateRoute requireModule="voice_calls"><VoiceCallsPage /></PrivateRoute>} />
            <Route path="live-assist-pro" element={<PrivateRoute requireModule="live_assist_pro"><LiveAssistProPage /></PrivateRoute>} />
            <Route path="live-assist-pro/:id" element={<PrivateRoute requireModule="live_assist_pro"><LiveAssistProPresenter /></PrivateRoute>} />
            <Route path="cctv" element={<PrivateRoute requireModule="cctv"><CCTVPage /></PrivateRoute>} />
            <Route path="video-wall" element={<PrivateRoute requireAdmin><VideoWallPage /></PrivateRoute>} />
            {/* Cuentas con proveedores — owner-only (la página redirige a cualquier otro; el backend responde 404) */}
            <Route path="supplier-accounts" element={<PrivateRoute requireAdmin><SupplierAccountsPage /></PrivateRoute>} />
            <Route path="my-platforms" element={<PrivateRoute requireAdmin><MyPlatformsPage /></PrivateRoute>} />
            <Route path="supplier-accounts/:id" element={<PrivateRoute requireAdmin><SupplierAccountsPage /></PrivateRoute>} />
            <Route path="offboarding" element={<PrivateRoute requireAdmin><OffboardingPage /></PrivateRoute>} />
            <Route path="website-reports" element={<PrivateRoute requireAdmin><WebsiteReportsPage /></PrivateRoute>} />
            <Route path="it-costs" element={<PrivateRoute requireAdmin><ItCostsPage /></PrivateRoute>} />
            <Route path="cockpit" element={<PrivateRoute requireAdmin><CockpitPage /></PrivateRoute>} />
            <Route path="campaigns" element={<PrivateRoute requireAdmin><CampaignsPage /></PrivateRoute>} />
            <Route path="data-integrity" element={<PrivateRoute requireAdmin><DataIntegrityPage /></PrivateRoute>} />
            <Route path="api-tokens" element={<PrivateRoute requireAdmin><ApiTokensPage /></PrivateRoute>} />
            <Route path="smart-homes" element={<PrivateRoute requireAdmin><SmartHomesPage /></PrivateRoute>} />
            <Route path="recruiting" element={<PrivateRoute requireAdmin><RecruitingPage /></PrivateRoute>} />
            <Route path="portal-studio" element={<PrivateRoute requireAdmin><PortalStudioPage /></PrivateRoute>} />
            <Route path="wa-avatars" element={<PrivateRoute requireAdmin><WhatsAppAvatarsPage /></PrivateRoute>} />
            <Route path="site-content" element={<PrivateRoute requireAdmin><SiteContentPage /></PrivateRoute>} />
            <Route path="2fa-codes" element={<PrivateRoute requireModule="2fa_codes"><TwoFaCodesPage /></PrivateRoute>} />
            <Route path="my-tickets" element={<MyTicketsPage />} />
            <Route path="tickets-private" element={<PrivateInboxPage />} />
            <Route path="tickets/review-queue" element={<BulkReviewQueuePage />} />
            <Route path="trash" element={<TrashPage />} />
            <Route path="spam" element={<SpamPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="inbound-rules" element={<PrivateRoute requireAdmin><InboundRulesPage /></PrivateRoute>} />
            <Route path="papelera" element={<PapeleraPage />} />
            <Route path="supplies" element={<SuppliesPage />} />
            <Route path="supplies/analytics" element={<SuppliesAnalyticsPage />} />
            <Route path="supplies/toner" element={<TonerInventoryPage />} />
            <Route path="supplies/:handle" element={<ClientSuppliesView view="printers" />} />
            <Route path="supplies/:handle/analytics" element={<ClientSuppliesView view="analytics" />} />
            <Route path="supplies/:handle/toner" element={<ClientSuppliesView view="toner" />} />
            <Route path="spare-parts" element={<Navigate to="/assets?tab=spare-parts" replace />} />
            <Route path="validation" element={<ValidationPage />} />
            <Route path="agents" element={<PrivateRoute requireManager><AgentsPage /></PrivateRoute>} />
            <Route path="portal-permissions" element={<PrivateRoute requireAdmin><PortalPermissionsPage /></PrivateRoute>} />
            <Route path="game-admin" element={<PrivateRoute requireAdmin><GameAdminPage /></PrivateRoute>} />
            <Route path="game" element={<PrivateRoute requireModule="game"><GamePage /></PrivateRoute>} />
            {/* Prode: mundial terminado — la propia página filtra por PRODE_USER_IDS (hoy Andy) */}
            <Route path="prode" element={<PrivateRoute><ProdePage /></PrivateRoute>} />
            <Route path="team" element={<PrivateRoute><TeamPanelPage /></PrivateRoute>} />
            <Route path="agent-status" element={<Navigate to="/team?tab=presence" replace />} />
            <Route path="agent-dashboards" element={<Navigate to="/team?tab=dashboards" replace />} />
            <Route path="agent-activity" element={<Navigate to="/team?tab=activity" replace />} />
            <Route path="usage-hotspots" element={<PrivateRoute requireAdmin><UsageHotspotsPage /></PrivateRoute>} />
            <Route path="session-replay" element={<PrivateRoute requireAdmin><SessionReplayPage /></PrivateRoute>} />
            <Route path="announcements" element={<PrivateRoute requireAdmin><AnnouncementsAdminPage /></PrivateRoute>} />
            <Route path="worker-health" element={<PrivateRoute requireAdmin><WorkerHealthPage /></PrivateRoute>} />
            <Route path="system-activity" element={<PrivateRoute requireAdmin><SystemActivityPage /></PrivateRoute>} />
            <Route path="hr" element={<PrivateRoute requireAdmin><HRPage /></PrivateRoute>} />
            <Route path="my-payroll" element={<PrivateRoute><MyPayrollPage /></PrivateRoute>} />
            <Route path="comms-monitor" element={<PrivateRoute requireModule="comms_monitor"><EmailMonitorPage /></PrivateRoute>} />
            <Route path="settings" element={<PrivateRoute requireModule="settings"><SettingsPage /></PrivateRoute>} />
            <Route path="chat" element={<PrivateRoute requireModule="chat"><ChatPage /></PrivateRoute>} />
            <Route path="kb" element={<PrivateRoute requireModule="knowledge_base"><KnowledgeBasePage /></PrivateRoute>} />
            <Route path="rag-search" element={<PrivateRoute requireModule="rag_search"><RagSearchPage /></PrivateRoute>} />
            <Route path="ai-dashboard" element={<PrivateRoute requireAdmin><AiDashboardPage /></PrivateRoute>} />
            <Route path="growth" element={<PrivateRoute requireAdmin><GrowthPage /></PrivateRoute>} />
            <Route path="kb/new" element={<PrivateRoute requireModule="knowledge_base"><KBArticleEditorPage /></PrivateRoute>} />
            <Route path="kb/:idOrSlug" element={<PrivateRoute requireModule="knowledge_base"><KBArticlePage /></PrivateRoute>} />
            <Route path="kb/:id/edit" element={<PrivateRoute requireModule="knowledge_base"><KBArticleEditorPage /></PrivateRoute>} />
            <Route path="invoicing" element={<PrivateRoute requireAdmin><InvoicingPage /></PrivateRoute>} />
            <Route path="billing-entities" element={<PrivateRoute requireAdmin><BillingEntitiesPage /></PrivateRoute>} />
            <Route path="accounts" element={<PrivateRoute requireAdmin><AccountsPage /></PrivateRoute>} />
            <Route path="accounts/:id" element={<PrivateRoute requireAdmin><AccountDetailPage /></PrivateRoute>} />
            <Route path="budgets" element={<PrivateRoute requireModule="budgets"><BudgetsPage /></PrivateRoute>} />
            <Route path="budgets/new" element={<PrivateRoute requireModule="budgets"><BudgetEditorPage /></PrivateRoute>} />
            <Route path="budgets/:id/edit" element={<PrivateRoute requireModule="budgets"><BudgetEditorPage /></PrivateRoute>} />
            <Route path="demo-visits" element={<PrivateRoute requireAdmin><DemoVisitsPage /></PrivateRoute>} />
            <Route path="vault" element={<PrivateRoute requireModule="vault"><VaultPage /></PrivateRoute>} />
            <Route path="calendar" element={<PrivateRoute requireModule="calendar"><CalendarPage /></PrivateRoute>} />
            <Route path="whatsapp-settings" element={<PrivateRoute requireModule="whatsapp"><WhatsAppSettingsPage /></PrivateRoute>} />
            <Route path="agent-ratings" element={<Navigate to="/team?tab=ratings" replace />} />
            <Route path="audit-log" element={<PrivateRoute requireModule="audit_log"><TicketAuditLogPage /></PrivateRoute>} />
            <Route path="deployments" element={<PrivateRoute requireModule="deployments"><DeploymentsPage /></PrivateRoute>} />
            <Route path="deployments/:projectId" element={<PrivateRoute requireModule="deployments"><DeploymentsPage /></PrivateRoute>} />
            <Route path="topology" element={<PrivateRoute requireModule="topology"><TopologyPage /></PrivateRoute>} />
            <Route path="topology/:id" element={<PrivateRoute requireModule="topology"><TopologyPage /></PrivateRoute>} />
            <Route path="resource-monitor" element={<PrivateRoute requireModule="resource_monitor"><ResourceMonitorPage /></PrivateRoute>} />
            <Route path="resource-monitor/:hostId" element={<PrivateRoute requireModule="resource_monitor"><ResourceMonitorPage /></PrivateRoute>} />
            <Route path="projects" element={<PrivateRoute requireModule="projects"><ProjectsPage /></PrivateRoute>} />
            <Route path="projects/:projectId" element={<PrivateRoute requireModule="projects"><ProjectsPage /></PrivateRoute>} />
            <Route path="backup-monitor" element={<PrivateRoute requireModule="backup_monitor"><BackupMonitorPage /></PrivateRoute>} />
            <Route path="ops-monitor" element={<PrivateRoute requireModule="ops_monitor"><OpsMonitorPage /></PrivateRoute>} />
            <Route path="cloud-costs" element={<PrivateRoute requireModule="cloud_costs"><CloudCostsPage /></PrivateRoute>} />
            <Route path="google-business" element={<PrivateRoute requireModule="google_business"><GoogleBusinessPage /></PrivateRoute>} />
            <Route path="tool-costs" element={<PrivateRoute requireAdmin><ToolCostsPage /></PrivateRoute>} />
            <Route path="costs" element={<PrivateRoute requireAdmin><CostsHubPage /></PrivateRoute>} />
            <Route path="dmarc" element={<PrivateRoute requireModule="dmarc"><DmarcPage /></PrivateRoute>} />
            <Route path="field-tasks" element={<PrivateRoute requireModule="errands"><ErrandsPage /></PrivateRoute>} />
            <Route path="requests" element={<PrivateRoute requireModule="requests"><RequestsPage /></PrivateRoute>} />
            <Route path="reports" element={<PrivateRoute requireModule="reports"><ReportsPage /></PrivateRoute>} />
            <Route path="report-review" element={<ReportReviewPage />} />
            <Route path="agent-actions" element={<PrivateRoute requireAdmin><AgentActionsPage /></PrivateRoute>} />
            <Route path="diagnostics" element={<PrivateRoute requireModule="diagnostics"><DiagnosticsAdminPage /></PrivateRoute>} />
            <Route path="assets" element={<PrivateRoute requireModule="assets"><AssetsPage /></PrivateRoute>} />
            <Route path="infrastructure" element={<InfrastructureRedirect />} />
            <Route path="workshop" element={<PrivateRoute requireModule="workshop"><WorkshopPage /></PrivateRoute>} />
            <Route path="prepaid-lines" element={<PrivateRoute requireModule="prepaid_lines"><PrepaidLinesPage /></PrivateRoute>} />
            <Route path="racks" element={<PrivateRoute requireModule="racks"><RacksPage /></PrivateRoute>} />
            <Route path="legacy-sheets" element={<PrivateRoute requireModule="legacy_sheets"><LegacySheetsPage /></PrivateRoute>} />
            <Route path="mercadolibre" element={<PrivateRoute requireModule="mercadolibre"><MercadoLibrePage /></PrivateRoute>} />
            <Route path="quick-launch" element={<PrivateRoute requireModule="quick_launch"><QuickLaunchPage /></PrivateRoute>} />
            <Route path="access-control" element={<PrivateRoute requireModule="access_control"><AccessControlPage /></PrivateRoute>} />
            <Route path="access-control/:rosterId" element={<PrivateRoute requireModule="access_control"><AccessControlPage /></PrivateRoute>} />
            <Route path="attendance" element={<PrivateRoute><AttendancePage /></PrivateRoute>} />
            <Route path="file-permissions" element={<PrivateRoute requireModule="file_permissions"><FilePermissionsPage /></PrivateRoute>} />
            <Route path="file-permissions/users/:principalEnc" element={<PrivateRoute requireModule="file_permissions"><FilePermissionsPage /></PrivateRoute>} />
            <Route path="file-permissions/:shareId" element={<PrivateRoute requireModule="file_permissions"><FilePermissionsPage /></PrivateRoute>} />
            <Route path="backup-monitor/:id" element={<PrivateRoute requireModule="backup_monitor"><BackupJobDetailPage /></PrivateRoute>} />
            <Route path="vps-health" element={<PrivateRoute requireModule="vps_health"><VpsHealthPage /></PrivateRoute>} />
            <Route path="vps-health/:id" element={<PrivateRoute requireModule="vps_health"><VpsHostDetailPage /></PrivateRoute>} />
            <Route path="ip-intelligence" element={<PrivateRoute requireModule="ip_intel"><IpIntelligencePage /></PrivateRoute>} />
            <Route path="configurations" element={<ConfigurationsPage />} />
            <Route path="email-accounts" element={<PrivateRoute requireModule="email_accounts"><EmailAccountsPage /></PrivateRoute>} />
            <Route path="monitoring" element={<PrivateRoute requireModule="monitoring"><MonitoringPage /></PrivateRoute>} />
            <Route path="monitoring/endpoints/:id" element={<PrivateRoute requireModule="monitoring"><MonitoringEndpointPage /></PrivateRoute>} />
            <Route path="monitoring/settings" element={<PrivateRoute requireModule="monitoring"><MonitoringSettingsPage /></PrivateRoute>} />
            <Route path="monitoring/team" element={<PrivateRoute requireModule="monitoring"><MonitoringTeamPage /></PrivateRoute>} />
            <Route path="monitoring/install" element={<PrivateRoute requireModule="monitoring"><MonitoringInstallPage /></PrivateRoute>} />
            <Route path="vpn" element={<PrivateRoute requireModule="vpn"><VPNPage /></PrivateRoute>} />
            <Route path="personnel" element={<PrivateRoute requireModule="personnel"><PersonnelPage /></PrivateRoute>} />
            <Route path="feedback" element={<PrivateRoute requireModule="feedback_requests"><FeedbackPage /></PrivateRoute>} />
            <Route path="feedback/admin" element={<PrivateRoute requireAdmin><FeedbackAdminPage /></PrivateRoute>} />
            <Route path="changelog" element={<PrivateRoute requireModule="changelog"><ChangelogPage /></PrivateRoute>} />
            <Route path="telegram-routes" element={<PrivateRoute requireAdmin><TelegramRoutesPage /></PrivateRoute>} />
            <Route path="procedures" element={<PrivateRoute requireModule="procedures"><ProceduresPage /></PrivateRoute>} />
            <Route path="provisioning" element={<PrivateRoute requireModule="provisioning"><ProvisioningPage /></PrivateRoute>} />
            <Route path="web-review" element={<PrivateRoute requireModule="web_review"><WebReviewPage /></PrivateRoute>} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </>
      )}
      <Route path="*" element={<Navigate to={isClientDomain ? '/' : '/'} replace />} />
    </Routes>
    </Suspense>
  );
}

function useToastPosition() {
  const [pos, setPos] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= 768 ? 'bottom-center' : 'top-right'
  );
  useEffect(() => {
    const onResize = () => setPos(window.innerWidth <= 768 ? 'bottom-center' : 'top-right');
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return pos;
}

// Cloudflare-style mobile toast: full-width, high-contrast colored bar pinned
// above the bottom nav with a clear icon. Every toast.success/error/loading call
// across the helpdesk routes through this single global Toaster, so making it
// prominent here makes feedback visible app-wide on phones automatically.
const MOBILE_TOAST_THEME = {
  success: { bg: 'var(--c-resolved)', icon: CheckCircle2 },
  error: { bg: '#ef4444', icon: XCircle },
  blank: { bg: 'var(--c-brand)', icon: Info },
  // Loading keeps the default spinner; dark slate bg so white text stays
  // readable in BOTH light and dark mode (surface-2 is near-white in light).
  loading: { bg: '#334155', icon: null },
};

function AppToaster() {
  const position = useToastPosition();
  const isMobile = position === 'bottom-center';
  return (
    <Toaster
      position={position}
      containerStyle={isMobile ? {
        left: 12, right: 12, bottom: 'calc(var(--mobile-bottom-nav, 60px) + var(--safe-bottom, 0px) + 12px)',
      } : undefined}
      toastOptions={{
        duration: 4000,
        style: isMobile
          ? { maxWidth: 'none', width: '100%', color: '#fff', borderRadius: '14px', fontSize: '15px', fontWeight: 600, padding: '14px 16px', boxShadow: '0 8px 28px rgba(0,0,0,0.35)' }
          : { background: 'var(--c-surface-2)', color: 'var(--c-text)', borderRadius: '12px', fontSize: '14px', border: '1px solid var(--c-border)' },
      }}
    >
      {(t) => {
        const theme = isMobile ? (MOBILE_TOAST_THEME[t.type] || MOBILE_TOAST_THEME.blank) : null;
        const MobileIcon = theme?.icon;
        const style = theme ? { ...t.style, background: theme.bg } : t.style;
        return (
          <ToastBar toast={t} style={style}>
            {({ icon, message }) => (
              <>
                {isMobile && t.type !== 'loading' && MobileIcon
                  ? <MobileIcon size={22} style={{ flexShrink: 0 }} />
                  : icon}
                <span style={{ flex: 1 }}>{message}</span>
                {t.type !== 'loading' && (
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: isMobile ? 'rgba(255,255,255,0.85)' : 'var(--c-text-muted)',
                      fontSize: 18, lineHeight: 1, padding: '0 0 0 4px', flexShrink: 0,
                    }}
                  >
                    &times;
                  </button>
                )}
              </>
            )}
          </ToastBar>
        );
      }}
    </Toaster>
  );
}

export default function App() {
  // Un solo listener para que TODOS los campos de fecha abran su calendario al
  // hacer click en cualquier parte, no sólo en el iconito. Ver utils/dateInput.
  useEffect(() => { installDatePickerOpener(); }, []);

  // Los avisos nativos de campo vacío salen en el idioma del navegador; esto los
  // pasa al idioma del helpdesk. Ver utils/formValidityI18n.
  useEffect(() => { installFormValidityI18n(); }, []);

  // Errores sueltos de JavaScript y promesas sin atrapar (incluye los "Loading chunk
  // failed"). Ver utils/reportClientError: los boundaries solos cubrían muy poco.
  useEffect(() => { installGlobalErrorReporting(); }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <LiveActivityPinger />
            <LiveCaptureAgent />
            <AppRoutes />
            <AppToaster />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
